import { useQuery } from 'react-query';
import { ApiClient, AgencyData } from '../../clients';
import { useConfig } from '../config';

type UseGetAgencyProps = {
  id: string | undefined;
};

type UseGetAgencyHook = {
  isGetAgencyLoading: boolean;
  isGetAgencyError: boolean;
  getAgencyError: Error | null;
  agencyData: AgencyData | undefined;
};

/**
 * Hook to get agency data given an id
 * Note: This is a temporary implementation that filters from getAllAgencies
 * until a dedicated getAgency endpoint is created
 */
export function useGetAgency({ id }: UseGetAgencyProps): UseGetAgencyHook {
  const config = useConfig();
  const apiClient = new ApiClient(config);

  const { isLoading, data, error, isError } = useQuery<AgencyData, Error>(
    `agency-${id}`,
    async () => {
      if (id) {
        // Get all agencies and filter by ID
        const allAgenciesResponse = await apiClient.getAllAgencies();
        const agency = allAgenciesResponse.data.find((agency) => agency.id === id);

        if (!agency) {
          throw new Error(`Agency with id ${id} not found`);
        }

        // Convert AgencyResource to AgencyData
        return {
          id: agency.id,
          name: agency.attributes.name,
          enabled: agency.attributes.enabled,
        };
      }
      throw new Error(`id not specified`);
    },
    {
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      enabled: id !== undefined,
    }
  );

  return {
    isGetAgencyLoading: isLoading,
    isGetAgencyError: isError,
    getAgencyError: error,
    agencyData: data,
  };
}
